import React, { useState, useEffect } from 'react';
import { twofaAPI } from '../services/api';
import BedrijfMfaSection from './BedrijfMfaSection';
import PortalMfaSection from './PortalMfaSection';

const accent = '#00D4AA';
const textMuted = '#94a3b8';
const cardBg = 'rgba(255,255,255,0.05)';
const cardBorder = '1.5px solid rgba(0,212,170,0.3)';

const inputStyle = {
  background: cardBg,
  color: '#fff',
  border: cardBorder,
  borderRadius: '10px',
  padding: '10px 14px',
  fontSize: '16px',
  width: '100%',
  boxSizing: 'border-box'
};

const codeInputStyle = {
  ...inputStyle,
  fontSize: '22px',
  letterSpacing: '6px',
  textAlign: 'center',
  fontFamily: 'monospace'
};

const buttonPrimary = {
  background: accent,
  color: '#0a0e1a',
  border: 'none',
  borderRadius: '10px',
  padding: '10px 20px',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer'
};

const buttonDanger = {
  background: 'transparent',
  color: '#ef4444',
  border: '1.5px solid #ef4444',
  borderRadius: '10px',
  padding: '8px 16px',
  fontSize: '14px',
  cursor: 'pointer'
};

const buttonSecondary = {
  background: 'transparent',
  color: accent,
  border: `1.5px solid ${accent}`,
  borderRadius: '10px',
  padding: '8px 16px',
  fontSize: '14px',
  cursor: 'pointer'
};

const card = {
  background: cardBg,
  border: cardBorder,
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '16px'
};

export default function MfaTab() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Setup-flow state
  const [setupActive, setSetupActive] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // Disable form
  const [showDisable, setShowDisable] = useState(false);
  const [disableWw, setDisableWw] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // Regenerate form
  const [showRegen, setShowRegen] = useState(false);
  const [regenCode, setRegenCode] = useState('');

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const r = await twofaAPI.status();
      setEnabled(r.data.totp_enabled);
    } catch (e) {
      setError('Kon status niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const startSetup = async () => {
    setError(''); setSuccess('');
    try {
      const r = await twofaAPI.setupStart();
      setQrDataUrl(r.data.qr_data_url);
      setSecret(r.data.secret);
      setSetupActive(true);
      setSetupCode('');
    } catch (e) {
      setError(e.response?.data?.message || 'Kon setup niet starten');
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const r = await twofaAPI.setupVerify(setupCode);
      setRecoveryCodes(r.data.recovery_codes);
      setSetupActive(false);
      setEnabled(true);
      setSuccess('2FA is geactiveerd! Bewaar de recovery codes goed.');
      setSetupCode('');
    } catch (e) {
      setError(e.response?.data?.message || 'Code ongeldig');
    }
  };

  const cancelSetup = () => {
    setSetupActive(false);
    setQrDataUrl(''); setSecret(''); setSetupCode('');
    setError('');
  };

  const doDisable = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await twofaAPI.disable(disableWw, disableCode);
      setEnabled(false);
      setShowDisable(false);
      setDisableWw(''); setDisableCode('');
      setSuccess('2FA is uitgeschakeld.');
      setRecoveryCodes([]);
    } catch (e) {
      setError(e.response?.data?.message || 'Uitschakelen mislukt');
    }
  };

  const doRegenerate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const r = await twofaAPI.regenerateRecovery(regenCode);
      setRecoveryCodes(r.data.recovery_codes);
      setShowRegen(false);
      setRegenCode('');
      setSuccess('Nieuwe recovery codes gegenereerd.');
    } catch (e) {
      setError(e.response?.data?.message || 'Genereren mislukt');
    }
  };

  if (loading) return <div style={{color: textMuted, padding: '20px'}}>Status laden...</div>;

  return (
    <div style={{maxWidth: '700px', margin: '0 auto', color: '#e2e8f0'}}>
      <h2 style={{color: '#1e293b', marginBottom: '8px'}}>🔐 Tweestapsverificatie (2FA)</h2>
      <p style={{color: textMuted, marginBottom: '24px'}}>
        Extra beveiliging voor je account via een Authenticator-app (Google Authenticator, Microsoft Authenticator, Authy).
      </p>

      {error && (
        <div style={{...card, background:'rgba(239,68,68,0.1)', borderColor:'#ef4444', color:'#fecaca'}}>
          {error}
        </div>
      )}
      {success && (
        <div style={{...card, background:'rgba(0,212,170,0.1)', borderColor:accent, color:accent}}>
          ✓ {success}
        </div>
      )}

      {/* SETUP-FLOW */}
      {setupActive && (
        <div style={card}>
          <h3 style={{color: accent, marginTop:0}}>1. Scan QR met je Authenticator-app</h3>
          <div style={{display:'flex', gap:'20px', flexWrap:'wrap', alignItems:'flex-start', marginBottom:'16px'}}>
            {qrDataUrl && (
              <img src={qrDataUrl} alt="QR code" style={{width:'200px', height:'200px', background:'#fff', padding:'8px', borderRadius:'8px'}} />
            )}
            <div style={{flex:1, minWidth:'200px'}}>
              <p style={{color: textMuted, fontSize:'13px', margin:'0 0 6px 0'}}>Geen QR-scanner? Voer deze sleutel handmatig in:</p>
              <code style={{display:'block', background:'rgba(0,0,0,0.3)', padding:'10px', borderRadius:'8px', fontSize:'12px', wordBreak:'break-all', color:'#fff'}}>
                {secret}
              </code>
            </div>
          </div>

          <h3 style={{color: accent}}>2. Voer de 6-cijferige code in</h3>
          <form onSubmit={verifySetup}>
            <input
              type="text"
              value={setupCode}
              onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              style={{...codeInputStyle, marginBottom: '12px'}}
              required
            />
            <div style={{display:'flex', gap:'10px'}}>
              <button type="submit" style={buttonPrimary} disabled={setupCode.length < 6}>
                Activeren
              </button>
              <button type="button" onClick={cancelSetup} style={buttonSecondary}>
                Annuleren
              </button>
            </div>
          </form>
        </div>
      )}

      {/* RECOVERY CODES (na verify of regenerate) */}
      {recoveryCodes.length > 0 && (
        <div style={{...card, borderColor: '#f59e0b', background: 'rgba(245,158,11,0.05)'}}>
          <h3 style={{color: '#f59e0b', marginTop:0}}>⚠️ Recovery codes — eenmalig getoond</h3>
          <p style={{color: textMuted, fontSize:'14px'}}>
            Bewaar deze codes in een password manager of print ze. Elke code is 1× bruikbaar als je geen toegang hebt tot je Authenticator-app.
          </p>
          <div style={{display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:'8px', marginBottom:'16px'}}>
            {recoveryCodes.map((c, i) => (
              <code key={i} style={{background:'rgba(0,0,0,0.3)', padding:'8px 12px', borderRadius:'6px', fontFamily:'monospace', fontSize:'15px', textAlign:'center', color:'#fff'}}>
                {c}
              </code>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(recoveryCodes.join('\n')); setSuccess('Recovery codes gekopieerd'); }}
            style={buttonSecondary}
          >
            📋 Kopiëren naar klembord
          </button>
          <button
            type="button"
            onClick={() => setRecoveryCodes([])}
            style={{...buttonSecondary, marginLeft:'8px'}}
          >
            Verbergen
          </button>
        </div>
      )}

      {/* HOOFD-STATUS */}
      {!setupActive && (
        <div style={card}>
          {enabled ? (
            <>
              <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px'}}>
                <span style={{fontSize:'28px'}}>✅</span>
                <div>
                  <div style={{color: accent, fontWeight: 600}}>2FA is actief</div>
                  <div style={{color: textMuted, fontSize:'14px'}}>Je account is beveiligd met tweestapsverificatie.</div>
                </div>
              </div>

              <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>
                {!showRegen && !showDisable && (
                  <>
                    <button onClick={() => setShowRegen(true)} style={buttonSecondary}>
                      🔄 Nieuwe recovery codes
                    </button>
                    <button onClick={() => setShowDisable(true)} style={buttonDanger}>
                      Uitschakelen
                    </button>
                  </>
                )}
              </div>

              {showRegen && (
                <form onSubmit={doRegenerate} style={{marginTop:'16px', borderTop:'1px solid rgba(255,255,255,0.1)', paddingTop:'16px'}}>
                  <p style={{color: textMuted, fontSize:'14px'}}>Voer een verse code uit je Authenticator-app in. Bestaande recovery codes worden ongeldig.</p>
                  <input
                    type="text"
                    value={regenCode}
                    onChange={(e) => setRegenCode(e.target.value.replace(/\D/g,''))}
                    placeholder="123456"
                    maxLength={6}
                    inputMode="numeric"
                    style={{...codeInputStyle, marginBottom:'12px'}}
                    required
                    autoFocus
                  />
                  <div style={{display:'flex', gap:'10px'}}>
                    <button type="submit" style={buttonPrimary} disabled={regenCode.length < 6}>Genereren</button>
                    <button type="button" onClick={() => { setShowRegen(false); setRegenCode(''); }} style={buttonSecondary}>Annuleren</button>
                  </div>
                </form>
              )}

              {showDisable && (
                <form onSubmit={doDisable} style={{marginTop:'16px', borderTop:'1px solid rgba(255,255,255,0.1)', paddingTop:'16px'}}>
                  <p style={{color:'#fecaca', fontSize:'14px'}}>⚠️ Hiermee verlies je een beveiligingslaag. Bevestig met wachtwoord + huidige code.</p>
                  <input
                    type="password"
                    value={disableWw}
                    onChange={(e) => setDisableWw(e.target.value)}
                    placeholder="Wachtwoord"
                    style={{...inputStyle, marginBottom:'10px'}}
                    required
                    autoFocus
                  />
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g,''))}
                    placeholder="6-cijferige code"
                    maxLength={6}
                    inputMode="numeric"
                    style={{...codeInputStyle, marginBottom:'12px'}}
                    required
                  />
                  <div style={{display:'flex', gap:'10px'}}>
                    <button type="submit" style={buttonDanger} disabled={!disableWw || disableCode.length < 6}>Uitschakelen</button>
                    <button type="button" onClick={() => { setShowDisable(false); setDisableWw(''); setDisableCode(''); }} style={buttonSecondary}>Annuleren</button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <>
              <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px'}}>
                <span style={{fontSize:'28px'}}>🔓</span>
                <div>
                  <div style={{color: '#1e293b', fontWeight: 600}}>2FA is uitgeschakeld</div>
                  <div style={{color: textMuted, fontSize:'14px'}}>Schakel het in voor extra beveiliging tegen ongeautoriseerde toegang.</div>
                </div>
              </div>
              <button onClick={startSetup} style={buttonPrimary}>
                🔐 2FA activeren
              </button>
            </>
          )}
        </div>
      )}

      <BedrijfMfaSection />
      <PortalMfaSection />
      <p style={{color: textMuted, fontSize:'13px', marginTop: '24px'}}>
        Aanbevolen apps: Google Authenticator, Microsoft Authenticator, Authy, 1Password (TOTP).
      </p>
    </div>
  );
}
